import { useCallback, useEffect, useRef, useState } from 'react';
import {
    fetchAvailability,
    fetchPermissionStatus,
    fetchStepsSummary,
    requestPermission,
} from '../bridge/healthBridge';
import { saveWalk, toRecDe } from '../api/walkAPI';
import {
    daysAgo,
    stepsToCalories,
    stepsToDistanceValue,
    toLocalDateStr,
} from '../utils/steps';
import { getUsrInfoId } from '../utils/user';

/**
 * 삼성 헬스 걸음수 조회 + 서버 동기화 훅.
 *
 * status 흐름:
 *   checking → unavailable        (삼성 헬스 미설치/OS 미지원, 또는 앱이 아님. 사유는 reason)
 *            → needPermission     (연동 전. 화면에서 connect() 로 동의 화면)
 *            → loading → ready    (정상)
 *            → error              (조회 실패)
 *
 * 서버 저장은 조회에 성공하면 곧바로, 화면을 막지 않고(await 하되 실패해도 무시) 이어서 한다.
 * 서버가 죽어도 걸음수 화면 자체는 보여야 하기 때문이다.
 */
export const STEPS_STATUS = Object.freeze({
    CHECKING: 'checking',
    UNAVAILABLE: 'unavailable',
    NEED_PERMISSION: 'needPermission',
    LOADING: 'loading',
    READY: 'ready',
    ERROR: 'error',
});

const REASON_MESSAGE = {
    APP_NOT_INSTALLED: '삼성 헬스 앱을 설치하면\n내 걸음수를 불러올 수 있어요.',
    OS_VERSION: '삼성 헬스 연동은 Android 10 이상에서만 지원합니다.',
    UNSUPPORTED_PLATFORM: '삼성 헬스 연동은 안드로이드에서만 지원합니다.',
    NOT_NATIVE: '앱에서만 걸음수를 불러올 수 있습니다.',
};

/**
 * 조회 실패 안내. SDK 원문(영어)은 사용자에게 보이지 않게 하고 숫자 코드만 붙인다
 * — 제보받을 때 원인을 잡을 단서다. 원문은 콘솔에 남긴다.
 */
const errorMessage = (e) => {
    console.warn('[steps]', e?.code, e?.message);
    const num = /^(\d+):/.exec(e?.message || '')?.[1];
    const suffix = num ? ` (${num})` : e?.code ? ` (${e.code})` : '';
    if (e?.code === 'TIMEOUT') return '삼성 헬스 응답이 없습니다.\n잠시 후 다시 시도해 주세요.';
    return `삼성 헬스 연동 중 문제가 발생했습니다.${suffix}`;
};

/**
 * 최초 연동 시 소급 저장할 일수.
 * 삼성 헬스는 과거 걸음수를 한 번에 주므로 첫 저장에서 30일을 채워 넣는다.
 */
const BACKFILL_DAYS = 30;

/**
 * 매번 서버에 다시 밀어 넣을 최근 일수.
 * 워치 동기화가 늦으면 지난 날짜 값이 나중에 바뀌므로 오늘 것만 보내면 옛 값이 굳는다.
 */
const REFRESH_DAYS = 7;

/** 소급을 이미 했는지. 사용자별로 구분해야 계정이 바뀌면 다시 소급한다. */
const backfillKey = () => `iitp.walk.backfilled.${getUsrInfoId()}`;

/**
 * 진행 중인 동기화. 훅을 쓰는 화면이 둘 이상 동시에 떠 있으면(홈 + 걸음수 대시보드)
 * 각자 동기화를 시작해 같은 요청이 중복으로 나간다. 특히 최초 소급은 30일치라
 * 실측에서 30건 POST 가 4번 나갔다. 앞선 요청에 올라타 한 번만 보낸다.
 */
let syncInFlight = null;

/**
 * 조회한 걸음수를 서버에 밀어 넣는다.
 * 화면 표시와 무관한 부수 작업이라 실패해도 화면 상태를 건드리지 않는다.
 * 컴포넌트 상태에 의존하지 않으므로 모듈 레벨에 둔다.
 */
async function runSync() {
    try {
        const backfilled = localStorage.getItem(backfillKey()) === '1';
        const spanDays = backfilled ? REFRESH_DAYS : BACKFILL_DAYS;

        // 화면이 7일만 볼 때도 서버에는 30일(최초) 또는 7일을 보내야 하므로 따로 조회한다.
        const summary = await fetchStepsSummary(
            toLocalDateStr(daysAgo(spanDays - 1)),
            toLocalDateStr(daysAgo(0)),
        );

        // 걸음이 0인 날은 보내지 않는다 — 기록이 없는 날까지 0으로 채우면
        // 전체 기록 목록이 빈 행으로 도배된다.
        const walkList = (summary || [])
            .filter((d) => d.steps > 0)
            .map((d) => ({
                recDe: toRecDe(d.date),
                steps: Math.round(d.steps),
                cnsumCalor: stepsToCalories(d.steps),
                walkDstnc: stepsToDistanceValue(d.steps),
            }));

        if (walkList.length === 0) return;

        await saveWalk(walkList);
        if (!backfilled) localStorage.setItem(backfillKey(), '1');
    } catch (e) {
        // 서버가 죽어도 걸음수 화면은 보여야 한다.
        console.warn('[walk] 서버 동기화 실패 —', e?.message);
    }
}

/** 이미 도는 중이면 그 요청에 올라탄다 (홈 + 대시보드 동시 마운트 대비). */
function syncToServer() {
    if (syncInFlight) return syncInFlight;
    syncInFlight = runSync().finally(() => {
        syncInFlight = null;
    });
    return syncInFlight;
}

export function useHealthSteps({ days = 7, autoLoad = true, sync = true } = {}) {
    const [status, setStatus] = useState(STEPS_STATUS.CHECKING);
    const [daily, setDaily] = useState([]);
    const [message, setMessage] = useState(null);
    const [reason, setReason] = useState(null);
    const [permissionDenied, setPermissionDenied] = useState(false);

    // 언마운트 후 setState 방지 (권한 동의 화면은 몇 분씩 열려 있을 수 있다)
    const aliveRef = useRef(true);
    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
        };
    }, []);


    const loadSteps = useCallback(async () => {
        if (!aliveRef.current) return;
        if (!autoLoad) {
            // 권한만 확인하면 되는 화면 — 불필요한 조회를 건너뛴다.
            setStatus(STEPS_STATUS.READY);
            return;
        }
        setStatus(STEPS_STATUS.LOADING);
        try {
            // 일자별 그룹 집계라 며칠치든 호출 1번이다.
            const summary = await fetchStepsSummary(
                toLocalDateStr(daysAgo(days - 1)),
                toLocalDateStr(daysAgo(0)),
            );
            if (!aliveRef.current) return;
            setDaily(
                (summary || []).map((d) => ({
                    date: d.date,
                    steps: Math.round(d.steps || 0),
                })),
            );
            setStatus(STEPS_STATUS.READY);

            if (sync) syncToServer();
        } catch (e) {
            if (!aliveRef.current) return;
            setMessage(errorMessage(e));
            setStatus(STEPS_STATUS.ERROR);
        }
    }, [days, autoLoad, sync]);

    /** 화면 진입 시 자동 판정. 동의 화면은 띄우지 않는다. */
    const check = useCallback(async () => {
        if (!aliveRef.current) return;
        setStatus(STEPS_STATUS.CHECKING);
        setMessage(null);
        setReason(null);
        try {
            const availability = await fetchAvailability();
            if (!aliveRef.current) return;
            if (!availability?.available) {
                const why = availability?.reason || 'OS_VERSION';
                setReason(why);
                setMessage(REASON_MESSAGE[why] || '삼성 헬스를 사용할 수 없습니다.');
                setStatus(STEPS_STATUS.UNAVAILABLE);
                return;
            }

            const permission = await fetchPermissionStatus();
            if (!aliveRef.current) return;
            if (!permission?.granted) {
                setStatus(STEPS_STATUS.NEED_PERMISSION);
                return;
            }

            await loadSteps();
        } catch (e) {
            if (!aliveRef.current) return;
            // 앱 밖(PC 브라우저)에서는 네이티브가 없으니 조회 실패가 아니라 미지원으로 본다.
            if (e.code === 'NOT_NATIVE') {
                setReason('NOT_NATIVE');
                setMessage(REASON_MESSAGE.NOT_NATIVE);
                setStatus(STEPS_STATUS.UNAVAILABLE);
                return;
            }
            setMessage(errorMessage(e));
            setStatus(STEPS_STATUS.ERROR);
        }
    }, [loadSteps]);

    /** "내 건강 데이터 연동하기" — 삼성 헬스 동의 팝업(걸음수·운동·혈압)을 띄운다. */
    const connect = useCallback(async () => {
        setPermissionDenied(false);
        try {
            const result = await requestPermission();
            if (!aliveRef.current) return false;
            if (!result?.granted) {
                setPermissionDenied(true);
                setStatus(STEPS_STATUS.NEED_PERMISSION);
                return false;
            }
            await loadSteps();
            return true;
        } catch (e) {
            if (!aliveRef.current) return false;
            setMessage(errorMessage(e));
            setStatus(
                e.code === 'NOT_NATIVE' || e.code === 'UNSUPPORTED_PLATFORM'
                    ? STEPS_STATUS.UNAVAILABLE
                    : STEPS_STATUS.ERROR,
            );
            return false;
        }
    }, [loadSteps]);

    useEffect(() => {
        check();
    }, [check]);

    return {
        status,
        daily,
        message,
        reason,
        permissionDenied,
        connect,
        reload: check,
    };
}
